import { useSelector } from 'react-redux'
import type { Constant } from '@pbt/pbt-ui-components'
import { moment } from '@pbt/pbt-ui-components'

import {
  getAutoshipUnit,
  getPrescriptionProductSizeUnit,
  getPrescriptionStates,
  getProductSizeUnit,
} from '~/store/reducers/constants'
import type { Prescription } from '~/types/entities/prescriptions'
import { getPrescriptionType } from '~/utils/prescription'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import {
  type DisplayField,
  getInstructionsField,
  getPrescriptionFlags,
  getPrescriptionStateName,
  getRxFields,
} from './rxCardUtilities'

interface UseRxContentOptions {
  itemDate?: string
  onDispenseRefill?: () => void
}

export interface RxContent {
  fields: DisplayField[]
  instructions: DisplayField
  isCustomView: boolean
  prescriptionStateName: string | null
  showInstructions: boolean
  showTime: string | null
}

const computeTimeDisplay = (
  itemDate: string | undefined,
  showTimeDisplay: boolean,
  timezone: string,
): string | null => {
  if (!showTimeDisplay || !itemDate) return null
  const m = moment.tz(itemDate, timezone)
  return m.isValid() ? m.fromNow() : null
}

/**
 * Hook that fetches Redux data and returns Rx fields and instructions for the card.
 */
export const useRxCardContent = (
  prescription: Prescription,
  options?: UseRxContentOptions,
): RxContent => {
  const { itemDate, onDispenseRefill } = options ?? {}

  const prescriptionStates = useSelector(getPrescriptionStates) as
    | Constant[]
    | undefined
  const prescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  ) as Constant[] | undefined
  const productSizeUnit = useSelector(getProductSizeUnit) as
    | Constant[]
    | undefined
  const autoshipUnit = useSelector(getAutoshipUnit) as Constant[] | undefined

  const { prescriptionType } = prescription

  const flags = getPrescriptionFlags(
    prescription,
    prescriptionType,
    prescriptionStates,
  )

  const { isInHouse } = getPrescriptionType(
    prescriptionType,
    prescription.origin,
  )
  const shouldEnableDispenseRefill = isInHouse && !flags.isCanceled

  const fields = getRxFields(prescription, prescriptionType, {
    prescriptionStates,
    prescriptionProductSizeUnit,
    productSizeUnit,
    autoshipUnit,
    onDispenseRefill: shouldEnableDispenseRefill ? onDispenseRefill : undefined,
  })

  const instructions = getInstructionsField(prescription)
  const prescriptionStateName = getPrescriptionStateName(
    prescription,
    prescriptionStates,
  )

  const isCustomView = flags.isDeclined || flags.isPendingVetApproval

  const timezone = useCurrentBusinessTimezone()
  const showTime = computeTimeDisplay(itemDate, isCustomView, timezone)

  const showInstructions = !isCustomView

  return {
    fields,
    instructions,
    isCustomView,
    prescriptionStateName,
    showInstructions,
    showTime,
  }
}
